import React from "react";
import MegaMenu from "../components/MegaMenu/MegaMenu";
import Footer from "../components/footer/Footer";
import ClinicFlowDiagram from "../components/AuraClinicComponents/ClinicFlowDiagram";
import PatientFlowDiagram from "../components/AuraClinicComponents/PatientFlowDiagram";
import PractitionerFlowDiagram from "../components/AuraClinicComponents/PractitionerFlowDiagram";
import SoftwareCompanyCta from "../components/software-company-cta/SoftwareCompanyCta";
import AuraContactForm from "../components/AuraClinicHomePageComponents/AuraContactForm/AuraContactForm";
import NewTestimonials from "../components/new-testimonials/NewTestimonials";
import TrendsInsights from "../components/trends-n-insights/TrendsInsights";

export default function AuraArchitecture() {
    // Colors converted from Tailwind palette to hex for visual parity
    const patientSteps = [
        {
            number: "1",
            title: "QR Code Scanning",
            color: "#000000", // gray-800
            items: [
                "Every clinic/hospital has its own unique QR code.",
                "Patient scans the QR code using their mobile phone.",
                "QR code opens the Clinic/Hospital Page inside Aura Clinic Portal.",
            ],
        },
        {
            number: "2",
            title: "Clinic/Hospital Page",
            color: "#000000",
            items: [
                "Page displays clinic details, practitioner list, and patient options.",
                "Patient must choose one of the following paths:",
                "• New Patient",
                "• Existing Patient",
            ],
        },
        {
            number: "3",
            title: "New Patient Flow",
            color: "#22c55e", // emerald-500
            subtitle: "(Triggered if the patient has never visited the clinic before)",
            items: [
                'Step 3.1 — Select "New Patient"',
                "Patient clicks New Patient button.",
                "System makes a READ request to BP APIs (using Medicare/Card Number or Phone) to check if patient already exists.",
                "",
                "Step 3.2 — Onboarding Form Opens",
                "New patient fills the full onboarding form:",
                "• Personal information",
                "• Medical information",
                "• Insurance details",
                "• Medicare details",
                "• Preferences",
                "• Documents upload (ID, insurance card, Medicare card, etc.)",
                "",
                "Step 3.3 — Write to BP",
                "Once patient submits the form, the portal sends a WRITE request to BP APIs.",
                "Patient record is created or updated inside BP.",
                "",
                "Step 3.4 — Redirect to Appointment Booking",
                "After successful onboarding, patient moves to Appointment Booking.",
            ],
        },
        {
            number: "4",
            title: "Existing Patient Flow",
            color: "#000000",
            subtitle: "(Triggered if patient already exists in the clinic's BP database)",
            items: [
                'Step 4.1 — Select "Existing Patient"',
                "Patient clicks Existing Patient button.",
                "",
                "Step 4.2 — Patient Login",
                "Patient logs in using:",
                "• Medicare/Card Number OR",
                "• Phone Number",
                "",
                "Step 4.3 — Read from BP",
                "Portal sends a READ request to BP APIs.",
                "Patient details and record are returned securely.",
                "System verifies and logs in the patient.",
                "",
                "Step 4.4 — Redirect to Patient Portal",
                "Patient enters the Patient Portal.",
            ],
        },
        {
            number: "5",
            title: "Appointment Booking Flow",
            color: "#f97316", // orange-500
            subtitle: "(New or Existing Patients)",
            items: [
                "Step 5.1 — Choose Practitioner",
                "Portal shows:",
                "• Available practitioners",
                "• Available dates",
                "• Available time slots",
                "(All fetched via READ from BP.)",
                "",
                "Step 5.2 — Book Appointment",
                "Patient selects practitioner, date, and time.",
                "Portal sends a WRITE request to BP APIs to create an appointment.",
                "",
                "Step 5.3 — Appointment Booked",
                "Patient gets confirmation:",
                "• Onscreen",
                "• Email/SMS (if enabled)",
            ],
        },
        {
            number: "6",
            title: "Patient Portal",
            color: "#3b82f6", // blue-500
            items: [
                "Once logged in, the patient can:",
                "",
                "Step 6.1 — View Upcoming Appointments",
                "Portal sends READ to BP and shows:",
                "• Upcoming appointments",
                "• Practitioner name",
                "• Appointment type",
                "• Time",
                "",
                "Step 6.2 — Access Check-in/Check-out Page",
                "Option appears if appointment is today.",
            ],
        },
        {
            number: "7",
            title: "Check-in / Check-out Flow",
            color: "#22c55e",
            items: [
                "Step 7.1 — Patient Check-in",
                "Patient taps Check-in inside the Portal.",
                "Portal sends WRITE request to BP API updating:",
                '• Patient status = "Arrived"',
                "Clinic or practitioner instantly sees the patient has arrived.",
                "",
                "Step 7.2 — Patient Check-out",
                "After appointment, patient taps Check-out.",
                "Portal sends WRITE request:",
                '• Patient status = "Left"',
                "System updates the presence status in BP.",
            ],
        },
        {
            number: "8",
            title: "Summary of BP API Interactions",
            color: "#f59e0b", // amber-500
            items: [
                "READ Operations:",
                "• Verify patient identity",
                "• Fetch demographics",
                "• Fetch appointment availability",
                "• Fetch upcoming appointments",
                "",
                "WRITE Operations:",
                "• Create/update onboarded patient",
                "• Create appointment booking",
                "• Update check-in status",
                "• Update check-out status",
            ],
        },
    ];

    const practitionerSteps = [
        {
            number: "1",
            title: "Practitioner Portal Access",
            color: "#000000",
            items: [
                "Practitioner opens the Practitioner Portal.",
                "Practitioner logs in using:",
                "• Email",
                "• Password",
                "Portal authenticates the user and establishes a secure session.",
            ],
        },
        {
            number: "2",
            title: "Retrieve Practitioner Data from BP",
            color: "#3b82f6",
            items: [
                "Once logged in, the system sends READ requests to BP APIs to retrieve:",
                "",
                "2.1 Today's Schedule",
                "• List of all appointments for the day",
                "• Appointment time, patient name, appointment type",
                "• Allows practitioner to prepare for the day",
                "",
                "2.2 Upcoming Appointments",
                "• Future appointments scheduled",
                "• Helps practitioner manage workload and planning",
            ],
        },
        {
            number: "3",
            title: "Retrieve Assigned Patients",
            color: "#22c55e",
            items: [
                "Practitioner selects an appointment or a patient from the list.",
                "The portal makes a READ request to BP APIs to fetch:",
                "",
                "3.1 Patient Profile (Basic Details)",
                "Contains patient demographics and identifiers.",
                "",
                "3.2 Medical History",
                "• Previous diagnoses",
                "• Medical conditions",
                "• Past encounters",
                "• Allergies & medications",
                "This uses another READ call to BP.",
                "",
                "3.3 Clinical Notes",
                "• Previous notes written by clinicians",
                "• Visit summaries",
                "• Reports",
                "This also comes via READ from BP.",
            ],
        },
        {
            number: "4",
            title: "AI Assistant Integration",
            color: "#a855f7", // purple-500
            items: [
                "Practitioner can generate a Pre-Consultation Summary.",
                "The AI Assistant uses patient clinical history + notes + medical records (retrieved securely) to produce:",
                "• A summarised patient profile",
                "• Condition overview",
                "• Risk factors",
                "• Medication insights",
                "• Talking points for the consultation",
                "",
                "AI never writes directly to BP; it only produces summarised insights for practitioner review.",
            ],
        },
        {
            number: "5",
            title: "Patient Check-In / Check-Out Visibility",
            color: "#000000",
            items: [
                "5.1 Check-In",
                "Portal retrieves check-in status from BP APIs (READ)",
                "Practitioner sees:",
                '• "Patient has arrived"',
                "• Check-in time",
                "Practitioner can start preparing for the consultation",
                "",
                "5.2 Check-Out",
                "Practitioner can update patient check-out time via WRITE to BP",
                "Status updates in real-time for admin and clinic dashboard",
            ],
        },
        {
            number: "6",
            title: "Clinical Notes Management",
            color: "#22c55e",
            items: [
                "After consultation, practitioner opens the Clinical Notes section.",
                "Practitioner writes or edits notes for the patient encounter.",
                "Portal sends a WRITE request to BP APIs to securely store:",
                "• Clinical notes",
                "• Observations",
                "• Diagnosis",
                "• Any additional documentation",
                "",
                "Notes become part of the patient's permanent medical record inside BP.",
            ],
        },
        {
            number: "7",
            title: "Practitioner Workflow Summary",
            color: "#f59e0b",
            items: [
                "Step → BP API Operation → Login",
                "Email + Password Auth (internal)",
                "",
                "Retrieve schedule → READ",
                "View upcoming appointments → READ",
                "Future bookings → READ",
                "Fetch patient data → Profile + Medical history → READ",
                "Use AI assistant → Generate summaries → No BP write",
                "Check-in/out visibility → Patient arrival status → READ",
                "Update check-out → Set check-out time → WRITE",
                "Write clinical notes → Add/edit clinical notes → WRITE",
            ],
        },
    ];

    const clinicSteps = [
        {
            number: "1",
            title: "Clinic Portal (Entry Point)",
            color: "#000000",
            items: [
                "Clinic staff log into the Clinic Portal to manage operations including:",
                "• QR codes",
                "• Patient management",
                "• Approvals",
                "• Services",
                "• Dashboard analytics",
                "• Practitioner assignments",
                "• MBS reviews",
                "• Follow-ups",
                "• Profile settings",
                "",
                "Each module interacts with BP APIs securely.",
            ],
        },
        {
            number: "2",
            title: "QR Code Management",
            color: "#000000",
            items: [
                "Purpose:",
                "Each clinic/hospital has a unique QR code for patient onboarding.",
                "",
                "Flow:",
                "1. Clinic portal generates or retrieves QR code.",
                "2. QR code contains clinic-specific identifier.",
                "3. When scanned → directs patients to the onboarding flow.",
                "",
                "BP Interaction:",
                "✓ READ (clinic details)",
                "✓ WRITE (QR metadata if required)",
            ],
        },
        {
            number: "3",
            title: "Patient Management",
            color: "#22c55e",
            items: [
                "Purpose:",
                "Clinic staff can view or manage their patient list.",
                "",
                "Flow:",
                "1. System retrieves assigned patients.",
                "2. Clinic staff can view:",
                "• Patient demographics",
                "• Medical history",
                "• Clinical notes",
                "• Appointment records",
                "",
                "BP Interaction:",
                "✓ READ – Patient List",
                "✓ READ – Medical History",
                "✓ READ – Clinical Notes",
                "✓ READ – Appointment Records",
            ],
        },
        {
            number: "4",
            title: "Approval Request Management",
            color: "#f97316",
            items: [
                "Purpose:",
                "Approve patient onboarding requests or updates.",
                "",
                "Flow:",
                "1. Clinic staff reviews pending onboarding requests.",
                "2. Staff can approve or reject.",
                "3. Approved data is written back to BP.",
                "",
                "BP Interaction:",
                "✓ READ – Pending Requests",
                "✓ WRITE – Approval Status Update",
            ],
        },
        {
            number: "5",
            title: "Service Management",
            color: "#3b82f6",
            items: [
                "Purpose:",
                "Manage clinic services, fees, durations, availability.",
                "",
                "Flow:",
                "1. Staff configures service list inside the portal.",
                "2. Portal writes relevant service information to BP (if BP supports service sync).",
                "3. Appointments use the standardized service list.",
                "",
                "BP Interaction:",
                "✓ READ – Existing Services",
                "✓ WRITE – New or Updated Services",
            ],
        },
        {
            number: "6",
            title: "Dashboard & Analytics",
            color: "#a855f7",
            items: [
                "Purpose:",
                "Clinic staff can see clinic performance metrics.",
                "",
                "Flow:",
                "1. Portal gathers data such as:",
                "• Total patients",
                "• Appointments",
                "• MBS review counts",
                "• Practitioner utilization",
                "2. Visual analytics rendered inside the Clinic Portal.",
                "",
                "BP Interaction:",
                "✓ READ – Appointment Trends",
                "✓ READ – Patient Counts",
                "✓ READ – Practitioner Activity",
            ],
        },
        {
            number: "7",
            title: "Practitioner Assignment",
            color: "#22c55e",
            items: [
                "Purpose:",
                "Assign patients to practitioners, or practitioners to clinics.",
                "",
                "Flow:",
                "1. Clinic staff selects practitioner.",
                "2. Assigns patients or appointment blocks.",
                "3. Updates BP records accordingly.",
                "",
                "BP Interaction:",
                "✓ READ – Practitioner List",
                "✓ WRITE – Assignment Updates",
            ],
        },
        {
            number: "8",
            title: "MBS Reviews",
            color: "#f97316",
            items: [
                "Purpose:",
                "Review patient Medicare benefit scheme (MBS) related data.",
                "",
                "Flow:",
                "1. Retrieve appointment + service details.",
                "2. Clinic validates compliance and billing eligibility.",
                "3. Final MBS review submitted.",
                "",
                "BP Interaction:",
                "✓ READ – Appointment Data",
                "✓ READ – Service Data",
                "✓ WRITE – Review Updates (if required)",
            ],
        },
        {
            number: "9",
            title: "Follow-Ups",
            color: "#000000",
            items: [
                "Purpose:",
                "Clinic can schedule follow-ups for patients.",
                "",
                "Flow:",
                "1. Staff selects a patient.",
                "2. Creates a follow-up reminder or appointment.",
                "3. Syncs with BP.",
                "",
                "BP Interaction:",
                "✓ READ – Patient Data",
                "✓ WRITE – Follow-up Appointment",
            ],
        },
        {
            number: "10",
            title: "Profile Settings",
            color: "#000000",
            items: [
                "Purpose:",
                "Manage clinic or user profile inside the portal.",
                "",
                "Flow:",
                "1. Staff can update profile settings (name, email, clinic info).",
                "2. Changes are saved in the portal and reflected in BP if linked.",
                "",
                "BP Interaction:",
                "✓ READ – Clinic Profile",
                "✓ WRITE – Profile Updates",
            ],
        },
        {
            number: "11",
            title: "Summary of BP API Interactions",
            color: "#f59e0b",
            items: [
                "Module/BP API Operation/Purpose:",
                "",
                "QR Code → READ / WRITE Clinic",
                "QR linking Patient Management → READ Patient, history, notes",
                "Approval Requests → READ + WRITE Onboarding approvals",
                "Service Management → READ + WRITE Clinic services",
                "Dashboard → READ Analytics & metrics",
                "Practitioner Assignment → READ + WRITE Practitioner-patient mapping",
                "MBS Reviews → READ / WRITE Billing validation",
                "Follow-Up → READ + WRITE Follow-up appointments",
                "Profile Settings → READ + WRITE Clinic profile updates",
            ],
        },
    ];

    const companyCta = {
        heading: "Ready to streamline your clinic?",
        subHeading: "Review Aura Clinic's impact on flow and MBS accuracy with a specialist.",
        buttonText: "Book a demo today",
    }

    const WorkflowStep = ({ step, isLast }) => (
        <div className="d-flex gap-3">
            <div className="d-flex flex-column align-items-center">
                <div
                    className="d-flex align-items-center justify-content-center rounded shadow-sm"
                    style={{
                        width: "32px",
                        height: "32px",
                        backgroundColor: step.color,
                        color: "#ffffff",
                        fontSize: "0.85rem",
                        fontWeight: 700,
                    }}
                >
                    {step.number}
                </div>
                {!isLast && (
                    <div
                        style={{
                            width: "2px",
                            flex: 1,
                            background: "linear-gradient(to bottom, #d1d5db, transparent)",
                            marginTop: "0.5rem",
                        }}
                    />
                )}
            </div>
            <div className="flex-grow-1 pb-4">
                <h4
                    className="mb-1"
                    style={{
                        fontSize: "1.125rem",
                        fontWeight: 700,
                        color: "#142C58",
                    }}
                >
                    {step.title}
                </h4>
                {step.subtitle && (
                    <p
                        className="mb-2 fst-italic"
                        style={{ fontSize: "0.875rem", color: "#6b7280" }}
                    >
                        {step.subtitle}
                    </p>
                )}
                <div>
                    {step.items.map((item, idx) => {
                        if (item === "") {
                            return (
                                <div
                                    key={idx}
                                    style={{ height: "0.5rem" }}
                                />
                            );
                        }

                        const isBullet =
                            item.startsWith("•") || item.startsWith("✓");

                        return (
                            <p
                                key={idx}
                                className="mb-1"
                                style={{
                                    fontSize: "0.875rem",
                                    color: isBullet ? "#4b5563" : "#374151",
                                    marginLeft: isBullet ? "0.5rem" : 0,
                                }}
                            >
                                {item}
                            </p>
                        );
                    })}
                </div>
            </div>
        </div>
    );

    return (
        <>
            <MegaMenu />

            {/* Header Section */}
            <section className="architecture-box-main">
                <div className="container-lg">
                    <div className="text-center mx-auto" style={{ maxWidth: "68rem" }}>
                        <h1
                            className="architecture-main-section-heading"
                        >
                            Platform Workflow Documentation
                        </h1>
                        <p
                            className="mb-0 description-text"
                        >
                            Comprehensive workflow steps for Patient, Practitioner, and Clinic
                            portals with BP API interactions
                        </p>
                    </div>
                </div>
            </section>

            {/* Patient Flow Section */}
            <section className="patient-flow-section-box">
                <div className="container-fluid">
                    {/* Section Header */}
                    <div className="mb-4">
                        <span
                            className="badge rounded-pill d-inline-flex align-items-center mb-3"
                            style={{
                                backgroundColor: "#d1fae5",
                                color: "#047857",
                                border: "1px solid #a7f3d0",
                                padding: "0.35rem 0.85rem",
                                fontSize: "0.8rem",
                                fontWeight: 600,
                            }}
                        >

                            Flow 1
                        </span>
                        <h2
                            className="architecture-main-heading"
                        >
                            Aura Clinic Patient Workflow Steps
                        </h2>
                        <p
                            className="mb-0"
                            style={{ color: "#4b5563" }}
                        >
                            (Modules → BP API Interaction → Outputs)
                        </p>
                    </div>

                    <div className="row g-4 align-items-start">
                        {/* Left - Diagram */}
                        <div className="col-lg-8 sticky-box-main">
                            <PatientFlowDiagram />
                        </div>

                        {/* Right - Steps */}
                        <div className="col-lg-4">
                            <div className="d-flex flex-column gap-2">
                                {patientSteps.map((step, idx) => (
                                    <WorkflowStep
                                        key={idx}
                                        step={step}
                                        isLast={idx === patientSteps.length - 1}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Practitioner Flow Section */}
            <section className="practitioner-flow-section">
                <div className="container-fluid">
                    {/* Section Header */}
                    <div className="mb-4">
                        <span
                            className="badge rounded-pill d-inline-flex align-items-center mb-3"
                            style={{
                                backgroundColor: "#dbeafe",
                                color: "#1d4ed8",
                                border: "1px solid #bfdbfe",
                                padding: "0.35rem 0.85rem",
                                fontSize: "0.8rem",
                                fontWeight: 600,
                            }}
                        >
                            {/* <Stethoscope
                                style={{
                                    width: "16px",
                                    height: "16px",
                                    marginRight: "0.5rem",
                                }}
                            /> */}
                            Flow 2
                        </span>
                        <h2
                            className="architecture-main-heading"
                        >
                            Aura Clinic Practitioner Workflow Steps
                        </h2>
                        <p className="description-text">
                            (Based on Diagram: Includes Practitioner Login → Schedule → Patient
                            Data → AI Summary → Clinical Notes)
                        </p>
                    </div>

                    <div className="row g-4 align-items-start">
                        {/* Left - Diagram */}
                        <div className="col-lg-8 sticky-box-main">
                            <PractitionerFlowDiagram />
                        </div>

                        {/* Right - Steps */}
                        <div className="col-lg-4">
                            <div className="d-flex flex-column gap-2">
                                {practitionerSteps.map((step, idx) => (
                                    <WorkflowStep
                                        key={idx}
                                        step={step}
                                        isLast={idx === practitionerSteps.length - 1}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Clinic Flow Section */}
            <section className="clinic-flow-section">
                <div className="container-fluid">
                    {/* Section Header */}
                    <div className="mb-4">
                        <span
                            className="badge rounded-pill d-inline-flex align-items-center mb-3"
                            style={{
                                backgroundColor: "#f3e8ff",
                                color: "#6b21a8",
                                border: "1px solid #e9d5ff",
                                padding: "0.35rem 0.85rem",
                                fontSize: "0.8rem",
                                fontWeight: 600,
                            }}
                        >
                            {/* <Building2
                                style={{
                                    width: "16px",
                                    height: "16px",
                                    marginRight: "0.5rem",
                                }}
                            /> */}
                            Flow 3
                        </span>
                        <h2
                            className="architecture-main-heading"
                            style={{ fontSize: "2.25rem", color: "#142C58" }}
                        >
                            Aura Clinic Clinic Portal Workflow
                        </h2>
                        <p
                            className="description-text"
                        >
                            (Modules → BP API Interaction → Outputs)
                        </p>
                    </div>

                    <div className="row g-4 align-items-start">
                        {/* Left - Diagram */}
                        <div className="col-lg-8 sticky-box-main">
                            <ClinicFlowDiagram />
                        </div>

                        {/* Right - Steps */}
                        <div className="col-lg-4">
                            <div className="d-flex flex-column gap-2">
                                {clinicSteps.map((step, idx) => (
                                    <WorkflowStep
                                        key={idx}
                                        step={step}
                                        isLast={idx === clinicSteps.length - 1}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <AuraContactForm />
            <NewTestimonials
                shortHeading={""}
                mainHeading={""}
                description={""}
                content={""}
            />
            <TrendsInsights customClass={"aura-blog-section"} category="aura-clinic" />
            <SoftwareCompanyCta content={companyCta} />
            <Footer />
        </>
    );
}